import axiosInstance from "@/lib/axios";
import { AxiosResponse } from "axios";

export interface PaginationParams {
  page?: number;
  limit?: number;
  sort?: string;
  order?: "asc" | "desc";
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export class BaseService<T = any> {
  protected endpoint: string;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  async getAll(
    params?: PaginationParams & Record<string, any>,
  ): Promise<PaginatedResponse<T>> {
    const response: AxiosResponse<PaginatedResponse<T>> =
      await axiosInstance.get(this.endpoint, { params });
    return response.data;
  }

  async getPaginated(
    params?: PaginationParams & Record<string, any>,
  ): Promise<PaginatedResponse<T>> {
    const response: AxiosResponse<PaginatedResponse<T>> =
      await axiosInstance.get(this.endpoint, { params });
    return response.data;
  }

  async getById(id: string | number): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.get(
      `${this.endpoint}/${id}`,
    );
    return response.data;
  }

  async create(data: Partial<T>): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.post(
      this.endpoint,
      data,
    );
    return response.data;
  }

  async update(id: string | number, data: Partial<T>): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.put(
      `${this.endpoint}/${id}`,
      data,
    );
    return response.data;
  }

  async patch(id: string | number, data: Partial<T>): Promise<T> {
    const response: AxiosResponse<T> = await axiosInstance.patch(
      `${this.endpoint}/${id}`,
      data,
    );
    return response.data;
  }

  async delete(id: string | number): Promise<void> {
    await axiosInstance.delete(`${this.endpoint}/${id}`);
  }

  async customRequest<R = any>(
    method: "get" | "post" | "put" | "patch" | "delete",
    path: string,
    data?: any,
    params?: any,
  ): Promise<R> {
    const url = `${this.endpoint}${path}`;
    const response: AxiosResponse<R> = await axiosInstance.request({
      method,
      url,
      data,
      params,
    });
    return response.data;
  }
}
